import { MessageContentComplex } from '@langchain/core/messages'
import { parsePartialJson } from '@langchain/core/output_parsers'
import { AIMessage, Checkpoint, Message } from '@langchain/langgraph-sdk'
import { LoadExternalComponent } from '@langchain/langgraph-sdk/react-ui'
import { Fragment } from 'react/jsx-runtime'
import { isAgentInboxInterruptSchema } from '../../../lib/agent-inbox-interrupt'
import { useStreamContext } from '../../../providers/Stream'
import { ThreadView } from '../agent-inbox'
import { MarkdownText } from '../MarkdownText'
import { getContentString } from '../utils'
import { GenericInterruptView } from './GenericInterrupt'
import { BranchSwitcher, CommandBar } from './Shared'
import { ToolCalls, ToolResult } from './ToolCalls'

function CustomComponent({ message, thread }: { message: Message; thread: ReturnType<typeof useStreamContext> }) {
    const { values } = useStreamContext()
    const customComponents = values.ui?.filter((ui) => ui.metadata?.message_id === message.id)

    if (!customComponents?.length) return null
    return (
        <Fragment key={message.id}>
            {customComponents.map((customComponent) => (
                <LoadExternalComponent key={customComponent.id} stream={thread} message={customComponent} meta={{ ui: customComponent }} />
            ))}
        </Fragment>
    )
}

function parseAnthropicStreamedToolCalls(content: MessageContentComplex[]): AIMessage['tool_calls'] {
    const toolCallContents = content.filter((c) => c.type === 'tool_use' && c.id)

    return toolCallContents.map((tc) => {
        const toolCall = tc as Record<string, any>
        let json: Record<string, any> = {}
        if (toolCall?.input) {
            try {
                json = parsePartialJson(toolCall.input) ?? {}
            } catch {
                // Pass
            }
        }
        return {
            name: toolCall.name ?? '',
            id: toolCall.id ?? '',
            args: json,
            type: 'tool_call'
        }
    })
}

interface InterruptProps {
    interruptValue?: unknown
    isLastMessage: boolean
    hasNoAIOrToolMessages: boolean
}

function Interrupt({ interruptValue, isLastMessage, hasNoAIOrToolMessages }: InterruptProps) {
    return (
        <>
            {isAgentInboxInterruptSchema(interruptValue) && (isLastMessage || hasNoAIOrToolMessages) && <ThreadView interrupt={interruptValue} />}
            {interruptValue && !isAgentInboxInterruptSchema(interruptValue) && isLastMessage ? (
                <GenericInterruptView interrupt={interruptValue} />
            ) : null}
        </>
    )
}

export function AssistantMessage({
    message,
    isLoading,
    handleRegenerate,
    hideToolCalls = false
}: {
    message: Message | undefined
    isLoading: boolean
    handleRegenerate: (parentCheckpoint: Checkpoint | null | undefined) => void
    hideToolCalls?: boolean
}) {
    const content = message?.content ?? []
    const contentString = getContentString(content)

    const thread = useStreamContext()
    const isLastMessage = thread.messages[thread.messages.length - 1].id === message?.id
    const hasNoAIOrToolMessages = !thread.messages.find((m) => m.type === 'ai' || m.type === 'tool')
    const meta = message ? thread.getMessagesMetadata(message) : undefined
    const threadInterrupt = thread.interrupt

    const parentCheckpoint = meta?.firstSeenState?.parent_checkpoint
    const anthropicStreamedToolCalls = Array.isArray(content) ? parseAnthropicStreamedToolCalls(content) : undefined

    const hasToolCalls = message && 'tool_calls' in message && message.tool_calls && message.tool_calls.length > 0
    const toolCallsHaveContents = hasToolCalls && message.tool_calls?.some((tc) => tc.args && Object.keys(tc.args).length > 0)
    const hasAnthropicToolCalls = !!anthropicStreamedToolCalls?.length
    const isToolResult = message?.type === 'tool'

    if (isToolResult && hideToolCalls) {
        return null
    }

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '6px',
                marginRight: 'auto'
            }}
        >
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0px'
                }}
            >
                {isToolResult ? (
                    <>
                        <ToolResult message={message} />
                        <Interrupt
                            interruptValue={threadInterrupt?.value}
                            isLastMessage={isLastMessage}
                            hasNoAIOrToolMessages={hasNoAIOrToolMessages}
                        />
                    </>
                ) : (
                    <>
                        {contentString.length > 0 && (
                            <div
                                style={{
                                    padding: '6px 12px',
                                    backgroundColor: 'var(--background)',
                                    borderRadius: '6px',
                                    width: '100%',
                                    maxWidth: '100%',
                                    overflowX: 'auto',
                                    position: 'relative',
                                    zIndex: 0
                                }}
                            >
                                <MarkdownText>{contentString}</MarkdownText>
                            </div>
                        )}

                        {!hideToolCalls && (
                            <>
                                {hasToolCalls && toolCallsHaveContents && <ToolCalls toolCalls={message.tool_calls} />}

                                {!toolCallsHaveContents && hasAnthropicToolCalls && <ToolCalls toolCalls={anthropicStreamedToolCalls} />}

                                {hasToolCalls && !toolCallsHaveContents && !hasAnthropicToolCalls && <ToolCalls toolCalls={message.tool_calls} />}
                            </>
                        )}

                        {message && <CustomComponent message={message} thread={thread} />}
                        <Interrupt
                            interruptValue={threadInterrupt?.value}
                            isLastMessage={isLastMessage}
                            hasNoAIOrToolMessages={hasNoAIOrToolMessages}
                        />
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                marginRight: 'auto',
                                opacity: 0.65,
                                transition: 'opacity 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                                ;(e.currentTarget as HTMLDivElement).style.opacity = '1'
                            }}
                            onMouseOut={(e) => {
                                ;(e.currentTarget as HTMLDivElement).style.opacity = '0.65'
                            }}
                        >
                            <BranchSwitcher
                                branch={meta?.branch}
                                branchOptions={meta?.branchOptions}
                                onSelect={(branch) => thread.setBranch(branch)}
                                isLoading={isLoading}
                            />
                            <CommandBar
                                content={contentString}
                                isLoading={isLoading}
                                isAiMessage={true}
                                handleRegenerate={() => handleRegenerate(parentCheckpoint)}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}

export function AssistantMessageLoading() {
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '6px',
                marginRight: 'auto'
            }}
        >
            <div
                style={{
                    display: 'flex',
                    height: '30px',
                    alignItems: 'center',
                    gap: '4px',
                    backgroundColor: 'var(--muted)',
                    borderRadius: '16px',
                    padding: '6px 12px'
                }}
            >
                <div
                    style={{
                        height: '6px',
                        width: '6px',
                        borderRadius: '50%',
                        backgroundColor: 'var(--foreground)',
                        opacity: 0.5,
                        animation: 'pulse 1.5s ease-in-out infinite'
                    }}
                ></div>
                <div
                    style={{
                        height: '6px',
                        width: '6px',
                        borderRadius: '50%',
                        backgroundColor: 'var(--foreground)',
                        opacity: 0.5,
                        animation: 'pulse 1.5s ease-in-out 0.5s infinite'
                    }}
                ></div>
                <div
                    style={{
                        height: '6px',
                        width: '6px',
                        borderRadius: '50%',
                        backgroundColor: 'var(--foreground)',
                        opacity: 0.5,
                        animation: 'pulse 1.5s ease-in-out 1s infinite'
                    }}
                ></div>
            </div>
        </div>
    )
}
